import { useState, useEffect, useRef } from "react";

/* ─────────────────────────────────────────────
   useScrollProgress
   Returns 0..1 as element crosses the viewport.
   Uses passive scroll event (not rAF loop) to
   avoid conflicts with Lenis smooth scroll.
─────────────────────────────────────────────── */
export function useScrollProgress(ref) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const calc = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = r.height + vh;
      const seen = vh - r.top;
      setP(Math.max(0, Math.min(1, seen / total)));
    };

    // Initial calculation
    calc();

    // Use scroll event — works with Lenis which dispatches synthetic scroll events
    window.addEventListener("scroll", calc, { passive: true });
    return () => window.removeEventListener("scroll", calc);
  }, [ref]);
  return p;
}

/* ─────────────────────────────────────────────
   useGlobalCursor
   Normalized -1..1 cursor relative to viewport
─────────────────────────────────────────────── */
export function useGlobalCursor() {
  const [c, setC] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      setC({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: (e.clientY / window.innerHeight) * 2 - 1,
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  return c;
}

/* ─────────────────────────────────────────────
   useRAF
   Seconds elapsed since component mount.
   Throttled to ~30fps to reduce React renders.
─────────────────────────────────────────────── */
export function useRAF() {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    let lastUpdate = 0;
    const loop = (now) => {
      // ~30fps throttle to reduce re-renders
      if (now - lastUpdate > 33) {
        setT((now - start) / 1000);
        lastUpdate = now;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

/* ─────────────────────────────────────────────
   useElementCursor
   Normalized -1..1 cursor relative to an element
─────────────────────────────────────────────── */
export function useElementCursor(ref) {
  const [t, setT] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      setT({
        x: Math.max(-1, Math.min(1, (e.clientX - cx) / (r.width / 2))),
        y: Math.max(-1, Math.min(1, (e.clientY - cy) / (r.height / 2))),
      });
    };
    const onLeave = () => setT({ x: 0, y: 0 });
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, [ref]);
  return t;
}
